import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  Eye,
  FileText,
  GraduationCap,
  Layers,
  Loader2,
  Pencil,
  Play,
  RefreshCw,
  Sparkles,
  Square,
  Trash2,
  Users,
  X,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { api, ApiError, type AdminResource } from "../../lib/api";
import { StatusBadge, SubjectBadge } from "../../components/ui/Badges";
import { ConfirmDialog } from "../../components/admin/AdminModals";
import { AdminChapterSplitModal } from "../../components/admin/AdminChapterSplitModal";
import { AdminModuleDraftModal } from "../../components/admin/AdminModuleDraftModal";
import { Modal } from "../../components/ui/Modal";
import { IngestedBadge } from "../../components/admin/IngestedBadge";
import { useIngestedMarkers } from "../../lib/useIngestedMarkers";
import { GeneratedContent } from "../../components/GeneratedContent";

export function AdminResourceDetailPage() {
  const { resourceId } = useParams<{ resourceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { tokens } = useAuth();
  const token = tokens?.accessToken ?? null;
  const ingested = useIngestedMarkers(token);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof api.admin.getResource>> | null>(null);
  const [content, setContent] = useState<Awaited<ReturnType<typeof api.admin.getResourceContent>> | null>(null);

  // Catalog data for editing
  const [courses, setCourses] = useState<{ id: string; name: string; code: string }[]>([]);
  const [universities, setUniversities] = useState<{ id: string; name: string }[]>([]);
  const [streams, setStreams] = useState<{ id: string; name: string }[]>([]);

  // Action states
  const [stopping, setStopping] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [reingesting, setReingesting] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishClass, setPublishClass] = useState<"final" | "mid" | "coc">("final");
  const [publishError, setPublishError] = useState("");

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editCourseId, setEditCourseId] = useState("");
  const [editUniId, setEditUniId] = useState("");
  const [editStreamId, setEditStreamId] = useState("");
  const [editFree, setEditFree] = useState(true);
  const [editYear, setEditYear] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);

  const fetchResource = useCallback(async (silent = false) => {
    if (!token || !resourceId) return;
    if (!silent) setLoading(true);
    setError("");
    try {
      const [resDetail, resContent] = await Promise.all([
        api.admin.getResource(token, resourceId),
        api.admin.getResourceContent(token, resourceId),
      ]);
      setDetail(resDetail);
      setContent(resContent);
      if (resContent.examClass) {
        setPublishClass(resContent.examClass);
      }
    } catch (err) {
      if (!silent) {
        setError(err instanceof ApiError ? err.message : "Failed to load resource details");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, resourceId]);

  // Initial load
  useEffect(() => {
    fetchResource();
  }, [fetchResource]);

  // Load catalog data for editing
  useEffect(() => {
    Promise.allSettled([api.courses(), api.universities(), api.streams()]).then(
      ([coursesRes, uniRes, streamRes]) => {
        if (coursesRes.status === "fulfilled") setCourses(coursesRes.value);
        if (uniRes.status === "fulfilled") setUniversities(uniRes.value);
        if (streamRes.status === "fulfilled") setStreams(streamRes.value);
      }
    );
  }, []);

  // Live polling when ingestion or extraction is active
  useEffect(() => {
    if (!token || !resourceId) return;
    const ingestionStatus = content?.ingestionStatus || detail?.ingestionStatus;
    const extractionStatus = (detail as (typeof detail & { extractionStatus?: string }))?.extractionStatus;
    const isBusy =
      ingestionStatus === "pending" ||
      ingestionStatus === "processing" ||
      extractionStatus === "queued" ||
      extractionStatus === "running";

    if (!isBusy) return;

    const interval = window.setInterval(() => {
      fetchResource(true);
    }, 2500);

    return () => window.clearInterval(interval);
  }, [token, resourceId, content?.ingestionStatus, detail, fetchResource]);

  // Back navigation preserving search filters
  const handleBack = () => {
    navigate(`/admin/resources${location.search || ""}`);
  };

  // Actions
  const handleStop = async () => {
    if (!token || !resourceId) return;
    setStopping(true);
    try {
      await api.admin.stopIngestion(token, resourceId);
      await fetchResource(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not stop ingestion");
    } finally {
      setStopping(false);
    }
  };

  const handleContinue = async () => {
    if (!token || !resourceId) return;
    setContinuing(true);
    try {
      await api.admin.continueIngestion(token, resourceId);
      await fetchResource(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not continue ingestion");
    } finally {
      setContinuing(false);
    }
  };

  const handleReingest = async () => {
    if (!token || !resourceId) return;
    setReingesting(true);
    try {
      await api.admin.reingestResource(token, resourceId);
      await fetchResource(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not re-process resource");
    } finally {
      setReingesting(false);
    }
  };

  const handleExtractModule = async () => {
    if (!token || !resourceId) return;
    setExtracting(true);
    try {
      await api.admin.extractModule(token, resourceId);
      await fetchResource(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not start module extraction");
    } finally {
      setExtracting(false);
    }
  };

  const handlePublishExam = async (notifyStudents = false) => {
    if (!token || !resourceId) return;
    setPublishing(true);
    setPublishError("");
    try {
      await api.admin.publishExam(token, resourceId, {
        examClass: publishClass,
        notifyStudents,
      });
      await fetchResource(true);
    } catch (err) {
      setPublishError(err instanceof ApiError ? err.message : "Publishing failed");
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublishExam = async () => {
    if (!token || !resourceId) return;
    setPublishing(true);
    setPublishError("");
    try {
      await api.admin.unpublishExam(token, resourceId);
      await fetchResource(true);
    } catch (err) {
      setPublishError(err instanceof ApiError ? err.message : "Unpublishing failed");
    } finally {
      setPublishing(false);
    }
  };

  const openEditModal = () => {
    if (!detail) return;
    setEditTitle(detail.title || content?.title || "");
    setEditCourseId(detail.courseId || content?.courseId || "");
    setEditUniId(detail.universityId || content?.universityId || "");
    setEditStreamId((detail as { streamId?: string })?.streamId || "");
    setEditFree(detail.isFree !== false && content?.isFree !== false);
    setEditYear(detail.examYear || content?.examYear || "");
    setEditError("");
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!token || !resourceId) return;
    if (!editTitle.trim()) {
      setEditError("Title is required");
      return;
    }
    setEditSaving(true);
    setEditError("");
    try {
      await api.admin.updateResource(token, resourceId, {
        title: editTitle.trim(),
        courseId: editCourseId || undefined,
        universityId: editUniId || undefined,
        streamId: editStreamId || null,
        isFree: editFree,
        examYear: editYear.trim(),
      });
      setShowEditModal(false);
      await fetchResource(true);
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : "Failed to update resource");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !resourceId) return;
    setDeleting(true);
    try {
      await api.admin.deleteResource(token, resourceId);
      navigate(`/admin/resources${location.search || ""}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to delete resource");
      setShowDeleteConfirm(false);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft size={14} /> Back to resources
        </button>
        <div className="flex flex-col items-center justify-center p-20 text-muted-foreground gap-3">
          <Loader2 size={24} className="animate-spin text-primary" />
          <p className="text-sm">Loading resource details…</p>
        </div>
      </div>
    );
  }

  if (error && !detail && !content) {
    return (
      <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition"
        >
          <ArrowLeft size={14} /> Back to resources
        </button>
        <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-6 text-center space-y-3">
          <p className="text-sm font-semibold text-destructive">{error}</p>
          <button
            type="button"
            onClick={() => fetchResource()}
            className="px-4 py-2 bg-primary text-on-accent rounded-lg text-xs font-semibold hover:bg-primary/90 transition inline-flex items-center gap-1.5"
          >
            <RefreshCw size={13} /> Try again
          </button>
        </div>
      </div>
    );
  }

  const title = detail?.title || content?.title || "Resource Details";
  const resourceType = content?.resourceType || (detail as { type?: string; resourceType?: string })?.resourceType || "resource";
  const ingestionStatus = content?.ingestionStatus || detail?.ingestionStatus || "completed";
  const extractionStatus = (detail as { extractionStatus?: string })?.extractionStatus;
  const isBusy =
    ingestionStatus === "pending" ||
    ingestionStatus === "processing" ||
    extractionStatus === "queued" ||
    extractionStatus === "running";

  const isModule = resourceType === "module";
  const isExam = resourceType === "previous_exam";
  const isFree = detail?.isFree ?? content?.isFree ?? true;
  const pageCount = content?.pageCount ?? detail?.pageCount;
  const questionCount = content?.questionCount ?? detail?.questionCount;
  const isPublished = content?.isPublished ?? detail?.isPublished;

  const currentCourse = courses.find((c) => c.id === (detail?.courseId || content?.courseId));
  const currentUniversity = universities.find((u) => u.id === (detail?.universityId || content?.universityId));
  const currentStream = streams.find((s) => s.id === (detail as { streamId?: string })?.streamId);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* ── Breadcrumb & Top Bar ── */}
      <div className="flex flex-col gap-2">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-muted-foreground">
          <Link to="/admin" className="hover:text-foreground transition">
            Dashboard
          </Link>
          <span>/</span>
          <Link to={`/admin/resources${location.search || ""}`} className="hover:text-foreground transition">
            Resources
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium truncate max-w-xs sm:max-w-md">
            {title}
          </span>
        </nav>

        <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold bg-secondary/80 hover:bg-secondary text-foreground transition"
          >
            <ArrowLeft size={13} />
            <span>Back to resources</span>
          </button>

          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {isModule && (
              <>
                {(!extractionStatus || extractionStatus === "not_requested") && (
                  <button
                    type="button"
                    onClick={handleExtractModule}
                    disabled={extracting}
                    className="px-3 py-1.5 text-xs font-semibold bg-primary hover:opacity-90 text-on-accent rounded-md inline-flex items-center gap-1.5 shadow-2xs transition disabled:opacity-50"
                  >
                    {extracting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    <span>Extract content</span>
                  </button>
                )}
                {extractionStatus === "failed" && (
                  <button
                    type="button"
                    onClick={handleExtractModule}
                    disabled={extracting}
                    className="px-3 py-1.5 text-xs font-semibold bg-destructive hover:bg-destructive/90 text-white rounded-lg inline-flex items-center gap-1.5 transition disabled:opacity-50"
                  >
                    {extracting ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                    <span>Retry extraction</span>
                  </button>
                )}
                {(extractionStatus === "extracted" || extractionStatus === "published" || extractionStatus === "rejected") && (
                  <button
                    type="button"
                    onClick={() => setShowDraftModal(true)}
                    className="px-3 py-1.5 text-xs font-semibold bg-secondary hover:bg-secondary/80 text-primary border border-primary/20 rounded-lg inline-flex items-center gap-1.5 transition"
                  >
                    <BookOpen size={13} />
                    <span>Review Draft</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowSplitModal(true)}
                  className="px-3 py-1.5 text-xs font-semibold border border-border rounded-lg hover:bg-muted text-foreground inline-flex items-center gap-1.5 transition"
                >
                  <Layers size={13} />
                  <span>Split chapters</span>
                </button>
              </>
            )}

            {!isModule && detail?.canStop && (
              <button
                type="button"
                onClick={handleStop}
                disabled={stopping}
                className="px-3 py-1.5 text-xs font-semibold border border-warning/30 bg-warning/10  text-warning rounded-lg inline-flex items-center gap-1.5 hover:bg-warning/10 transition disabled:opacity-50"
              >
                {stopping ? <Loader2 size={13} className="animate-spin" /> : <Square size={12} />}
                <span>Stop</span>
              </button>
            )}

            {!isModule && detail?.canContinue && (
              <button
                type="button"
                onClick={handleContinue}
                disabled={continuing}
                className="px-3 py-1.5 text-xs font-semibold bg-primary text-on-accent rounded-lg inline-flex items-center gap-1.5 hover:bg-primary/90 transition disabled:opacity-50"
              >
                {continuing ? <Loader2 size={13} className="animate-spin" /> : <Play size={12} />}
                <span>Continue</span>
              </button>
            )}

            {!isModule && detail?.canReingest && (
              <button
                type="button"
                onClick={handleReingest}
                disabled={reingesting}
                className="px-3 py-1.5 text-xs font-semibold border border-border rounded-lg hover:bg-muted text-foreground inline-flex items-center gap-1.5 transition disabled:opacity-50"
              >
                {reingesting ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={12} />}
                <span>Retry</span>
              </button>
            )}

            <button
              type="button"
              onClick={openEditModal}
              className="px-3 py-1.5 text-xs font-semibold border border-border rounded-lg hover:bg-muted text-foreground inline-flex items-center gap-1.5 transition"
            >
              <Pencil size={13} />
              <span>Edit</span>
            </button>

            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10 rounded-lg inline-flex items-center gap-1.5 transition"
            >
              <Trash2 size={13} />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive">
          {error}
        </div>
      )}

      {/* ── Main Header Card ── */}
      <div className="bg-card rounded-xl border border-border p-5 sm:p-6 space-y-4 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="space-y-1.5 min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground break-words">
              {title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider bg-secondary text-primary">
                {resourceType.replace("_", " ")}
              </span>
              <StatusBadge status={ingestionStatus} />
              {resourceId && ingested.past.has(resourceId) && <IngestedBadge />}
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                  isFree ? "bg-muted text-muted-foreground" : "bg-warning/10 text-warning"
                }`}
              >
                {isFree ? "Free" : "Premium"}
              </span>
              {currentStream && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-primary/10 text-primary">
                  {currentStream.name}
                </span>
              )}
              {isBusy && (
                <span className="text-xs text-info flex items-center gap-1 font-medium">
                  <Loader2 size={12} className="animate-spin" /> Updating live
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Ingestion error alert */}
        {(content?.ingestionError || detail?.ingestionError) && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-xs text-destructive">
            <p className="font-semibold">Processing error:</p>
            <p className="mt-0.5">{content?.ingestionError || detail?.ingestionError}</p>
          </div>
        )}

        {/* Metadata Details Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-3 border-t border-border text-xs">
          <div>
            <span className="text-muted-foreground block text-xs uppercase tracking-wide">Course</span>
            <span className="font-medium text-foreground mt-0.5 block truncate">
              {currentCourse ? `${currentCourse.name} (${currentCourse.code})` : "—"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground block text-xs uppercase tracking-wide">Subject</span>
            <div className="mt-0.5">
              <SubjectBadge subject={(detail as { subject?: string })?.subject || ""} />
            </div>
          </div>
          <div>
            <span className="text-muted-foreground block text-xs uppercase tracking-wide">University</span>
            <span className="font-medium text-foreground mt-0.5 block truncate">
              {currentUniversity ? currentUniversity.name : "Common / All"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground block text-xs uppercase tracking-wide">Pages</span>
            <span className="font-medium text-foreground mt-0.5 block">
              {pageCount != null ? `${pageCount} page${pageCount === 1 ? "" : "s"}` : "—"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground block text-xs uppercase tracking-wide">Questions</span>
            <span className="font-medium text-foreground mt-0.5 block">
              {questionCount != null ? questionCount : "—"}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground block text-xs uppercase tracking-wide">Exam Year</span>
            <span className="font-medium text-foreground mt-0.5 block">
              {detail?.examYear || content?.examYear || "—"}
            </span>
          </div>
        </div>

        {/* Per-file extraction results if multi-file */}
        {content?.extractionFiles && content.extractionFiles.length > 0 && (
          <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3 space-y-1.5 text-xs">
            <p className="font-semibold text-muted-foreground uppercase tracking-wide text-xs">
              Multi-file extraction breakdown
            </p>
            <div className="space-y-1">
              {content.extractionFiles.map((f, i) => {
                const ok = f.status === "ok" || (!f.status && !f.failed);
                return (
                  <div key={i} className={`flex items-center gap-2 ${ok ? "text-success" : "text-destructive"}`}>
                    <span>{ok ? "✓" : "✗"}</span>
                    <span className="font-medium">{f.file_name || `File ${i + 1}`}</span>
                    {ok && f.page_count != null && <span className="text-muted-foreground text-xs">({f.page_count} pages)</span>}
                    {!ok && f.error && <span className="text-xs">· {f.error}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Access Statistics ── */}
      {detail?.accessStats && (
        <div className="space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Usage & Engagement</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Total views", value: detail.accessStats.views, icon: Eye },
              { label: "Unique students", value: detail.accessStats.uniqueViewers, icon: Users },
              { label: "Downloads", value: detail.accessStats.downloads, icon: Download },
              { label: "Practice sessions", value: detail.accessStats.practices, icon: CheckCircle },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-border bg-card p-4 shadow-2xs">
                <div className="flex items-center justify-between text-muted-foreground mb-1">
                  <span className="text-xs">{s.label}</span>
                  <s.icon size={15} />
                </div>
                <p className="text-xl font-bold text-foreground">{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Recent Access Log (Parent-level scrolling, naturally flows) ── */}
      {detail?.recentAccess && detail.recentAccess.length > 0 && (
        <div className="rounded-xl border border-border bg-card overflow-hidden shadow-2xs">
          <div className="px-4 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Clock size={13} /> Recent Student Access
            </h2>
            <span className="text-xs text-muted-foreground">{detail.recentAccess.length} recent events</span>
          </div>
          <div className="divide-y divide-border">
            {detail.recentAccess.map((row, i) => (
              <div key={`${row.userId}-${row.at}-${i}`} className="px-4 py-2.5 flex items-center justify-between text-xs hover:bg-muted/30 transition">
                <span className="font-medium text-foreground">{row.userName}</span>
                <span className="text-muted-foreground">
                  <span className="capitalize font-medium">{row.action}</span> · {new Date(row.at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Exam Publishing Controls (for past exams) ── */}
      {isExam && ingestionStatus === "completed" && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-4 shadow-2xs">
          <div className="border-b border-border pb-3">
            <h2 className="font-bold text-base text-foreground">Exam Publication</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Manage student availability and Telegram broadcast.
            </p>
          </div>

          {isPublished ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-success font-medium">
                <CheckCircle size={15} />
                <span>
                  Published as {(content?.examClass || detail?.examClass || "exam").toUpperCase()}
                  {content?.publishedAt ? ` · ${new Date(content.publishedAt).toLocaleString()}` : ""}
                </span>
              </div>
              <button
                type="button"
                onClick={handleUnpublishExam}
                disabled={publishing}
                className="px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10 rounded-lg transition disabled:opacity-50"
              >
                {publishing ? "Working…" : "Unpublish (hide from students)"}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Classify this exam before publishing. Students will only see this exam in their practice section once published.
              </p>
              <div className="flex items-center gap-2">
                {(["final", "mid", "coc"] as const).map((cls) => (
                  <button
                    key={cls}
                    type="button"
                    onClick={() => setPublishClass(cls)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      publishClass === cls
                        ? "bg-primary text-on-accent border-primary"
                        : "bg-card border-border text-muted-foreground hover:border-primary/40"
                    }`}
                  >
                    {cls === "final" ? "Final Exam" : cls === "mid" ? "Midterm Exam" : "COC Exam"}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => handlePublishExam(false)}
                  disabled={publishing}
                  className="px-4 py-2 bg-primary text-on-accent text-xs font-semibold rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
                >
                  {publishing ? "Publishing…" : "Publish exam"}
                </button>
                <button
                  type="button"
                  onClick={() => handlePublishExam(true)}
                  disabled={publishing}
                  className="px-4 py-2 border border-border text-xs font-semibold rounded-lg hover:bg-muted text-foreground transition disabled:opacity-50"
                >
                  Publish & notify Telegram channel
                </button>
              </div>
              {publishError && <p className="text-xs text-destructive">{publishError}</p>}
            </div>
          )}
        </div>
      )}

      {/* ── Extracted Content Section (Natural height, no nested scrollbar) ── */}
      <div className="rounded-xl border border-border bg-card p-5 sm:p-6 space-y-4 shadow-xs">
        <div className="border-b border-border pb-3 flex items-center justify-between">
          <h2 className="font-bold text-base text-foreground flex items-center gap-2">
            <FileText size={17} className="text-primary" />
            <span>Extracted Content</span>
          </h2>
          {content?.questions && content.questions.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {content.questions.length} question{content.questions.length === 1 ? "" : "s"}
            </span>
          )}
        </div>

        {isBusy ? (
          <div className="py-12 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <Loader2 size={24} className="animate-spin text-primary" />
            <p className="text-sm font-medium">Extraction in progress…</p>
            <p className="text-xs">This page updates automatically as content is processed.</p>
          </div>
        ) : content?.questions && content.questions.length > 0 ? (
          <div className="space-y-4">
            {content.questions.map((q, idx) => (
              <div key={idx} className="p-4 rounded-lg border border-border bg-muted/20 space-y-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-medium text-sm text-foreground flex-1">
                    <span className="text-primary font-bold mr-1.5">
                      {q.number != null ? `${q.number}.` : `${idx + 1}.`}
                    </span>
                    {q.text}
                  </div>
                  {q.question_type && (
                    <span className="text-xs uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-secondary text-primary shrink-0">
                      {q.question_type.replace("_", " ")}
                    </span>
                  )}
                </div>

                {q.options && q.options.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    {q.options.map((opt) => {
                      const isCorrect = q.answer?.label?.trim().toUpperCase() === opt.label.trim().toUpperCase();
                      return (
                        <div
                          key={opt.label}
                          className={`p-2.5 rounded-md text-xs border flex items-start gap-2 ${
                            isCorrect
                              ? "border-success/30 bg-success/10/70  text-success font-medium"
                              : "border-border/60 bg-card text-foreground"
                          }`}
                        >
                          <span className="font-bold shrink-0">{opt.label})</span>
                          <span>{opt.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {q.answer?.text && (
                  <div className="pt-2 border-t border-border/60 text-xs text-success font-medium">
                    Answer: {q.answer.label ? `(${q.answer.label}) ` : ""}{q.answer.text}
                  </div>
                )}

                {q.answer?.explanation && (
                  <div className="text-xs text-muted-foreground bg-muted/40 p-2.5 rounded-md border border-border/40 italic">
                    <span className="font-semibold not-italic text-foreground">Explanation: </span>
                    {q.answer.explanation}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : content?.plainText ? (
          <div className="p-4 rounded-lg bg-muted/40 border border-border">
            <pre className="text-xs font-mono whitespace-pre-wrap break-words leading-relaxed text-foreground">
              {content.plainText}
            </pre>
          </div>
        ) : (
          <div className="py-12 text-center text-muted-foreground space-y-2">
            <p className="text-sm">No extracted content yet.</p>
            {isModule && (
              <p className="text-xs">
                Click &ldquo;Extract content&rdquo; above to parse chapters and study materials from this module.
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Edit Metadata Modal ── */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Resource Metadata"
        size="md"
      >
        <div className="space-y-4">

            {editError && (
              <div className="p-2.5 bg-destructive/10 border border-destructive/20 rounded-lg text-xs text-destructive">
                {editError}
              </div>
            )}

            <div className="space-y-3 text-xs">
              <div>
                <label className="block font-medium mb-1 text-foreground">Title</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block font-medium mb-1 text-foreground">Course</label>
                <select
                  value={editCourseId}
                  onChange={(e) => setEditCourseId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:border-primary"
                >
                  <option value="">Select a course</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-medium mb-1 text-foreground">University</label>
                <select
                  value={editUniId}
                  onChange={(e) => setEditUniId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:border-primary"
                >
                  <option value="">Common (All universities)</option>
                  {universities.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-medium mb-1 text-foreground">Stream</label>
                <select
                  value={editStreamId}
                  onChange={(e) => setEditStreamId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:border-primary"
                >
                  <option value="">Common (No specific stream)</option>
                  {streams.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {isExam && (
                <div>
                  <label className="block font-medium mb-1 text-foreground">Exam Year</label>
                  <input
                    type="text"
                    value={editYear}
                    onChange={(e) => setEditYear(e.target.value)}
                    placeholder="e.g. 2023"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm outline-none focus:border-primary"
                  />
                </div>
              )}

              <div className="pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFree}
                    onChange={(e) => setEditFree(e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="font-medium text-foreground">Free resource (available to all users)</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="px-3.5 py-1.5 text-xs font-semibold rounded-lg border border-border hover:bg-muted text-foreground transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary text-on-accent hover:bg-primary/90 transition disabled:opacity-50"
              >
                {editSaving ? "Saving…" : "Save changes"}
              </button>
            </div>
        </div>
      </Modal>

      {/* ── Modals: Chapter Split, Draft Review, Delete ── */}
      {resourceId && (
        <>
          <AdminChapterSplitModal
            isOpen={showSplitModal}
            resourceId={resourceId}
            token={token || ""}
            onClose={() => setShowSplitModal(false)}
            onConfirmed={() => {
              fetchResource(true);
            }}
          />

          <AdminModuleDraftModal
            open={showDraftModal}
            moduleId={resourceId}
            token={token || ""}
            onClose={() => setShowDraftModal(false)}
            onUpdated={() => {
              fetchResource(true);
            }}
          />
        </>
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete Resource?"
        description={`Are you sure you want to delete "${title}"? All associated chapters, questions, and content will be permanently removed.`}
        confirmText="Delete permanently"
        variant="destructive"
        busy={deleting}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}
